import { Fragment } from "react";
import { Table, TableBody, TableRow, useTheme } from "@mui/material";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type {
  ExtendedVisionUser,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import type { apiHooks } from "../../../api/index.js";
import { DataCell } from "../../../components/IncidentWorkItems/IncidentTable/cells/DataCell.js";
import { columns } from "../../../components/IncidentWorkItems/IncidentTable/columns.js";
import {
  ColumnHeaderRow,
  EmptySectionRow,
  LoadingSectionRow,
  PendingTransferBannerRow,
} from "../../../components/IncidentWorkItems/IncidentTable/rows/index.js";
import type { SectionConfig } from "../../../components/IncidentWorkItems/IncidentTable/rows/SectionSubHeaderRow.js";
import { SectionSubHeaderRow } from "../../../components/IncidentWorkItems/IncidentTable/rows/SectionSubHeaderRow.js";
import { useIncidentActions } from "../useIncidentActions.js";
import { buildRows } from "./buildRows.js";

export type DesktopIncidentTableProps = {
  user: ExtendedVisionUser | undefined;
  isLoading: boolean;
  myIncidents: IncidentWorkItemWithContext[];
  transferredIncidents: IncidentWorkItemWithContext[];
  refetchWorkItems: ReturnType<
    typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
  >[1];
};

export function DesktopIncidentTable({
  user,
  isLoading,
  myIncidents,
  transferredIncidents,
  refetchWorkItems,
}: DesktopIncidentTableProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    isPendingTransfer,
    canCancelTransfer,
    handleViewIncident,
    handleSecondaryAction,
  } = useIncidentActions(refetchWorkItems);

  // Build the rows for the table
  const buildArgs = {
    user,
    isPendingTransfer,
    canCancelTransfer,
    handleViewIncident,
    handleSecondaryAction,
    t,
  };

  const myRows = buildRows({
    mode: "myIncidents",
    incidents: myIncidents,
    ...buildArgs,
  });

  const transferredRows = buildRows({
    mode: "transferredIncidents",
    incidents: transferredIncidents,
    ...buildArgs,
  });

  // We use all rows for the TanStack table instance so column sizing
  // considers data from both sections.
  const allRows = [...myRows, ...transferredRows];

  const table = useReactTable({
    data: allRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    // `data` is rebuilt on every render, and each new array would queue a
    // page-index reset whose state update re-renders again, endlessly.
    autoResetPageIndex: false,
  });

  const colSpan = columns.length;

  const myConfig = {
    titleLabel: tk("incidentWorkItem.myIncidents"),
    emptyLabel: tk("incidentWorkItem.yourIncidentsHere"),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  } satisfies SectionConfig;

  const transferredConfig = {
    titleLabel: tk("incidentWorkItem.transferredIncidents"),
    emptyLabel: tk("incidentWorkItem.transferredIncidentsHere"),
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  } satisfies SectionConfig;

  return (
    <Table
      sx={{
        tableLayout: "auto",
        width: "100%",
        borderCollapse: "collapse",
        "& .MuiTableCell-root": {
          padding: "0.5rem 0.75rem",
          wordBreak: "break-word",
        },
        "& .structural-row .MuiTableCell-root": {
          border: "none",
          padding: 0,
        },
      }}
    >
      <TableBody>
        {/* ---- My Incidents section ---- */}
        <SectionSubHeaderRow
          config={myConfig}
          colSpan={colSpan}
          {...(isLoading
            ? { type: "loading" }
            : { type: "loaded", count: myIncidents.length })}
        />

        {isLoading ? (
          <LoadingSectionRow colSpan={colSpan} />
        ) : myRows.length === 0 ? (
          <EmptySectionRow label={myConfig.emptyLabel} colSpan={colSpan} />
        ) : (
          <>
            <ColumnHeaderRow table={table} />
            {myRows.map((row) => {
              const tableRow = table
                .getRowModel()
                .rows.find((r) => r.id === row.id);
              if (!tableRow) return null;

              const cells = tableRow.getVisibleCells();

              return (
                <Fragment key={tableRow.id}>
                  {row.isPendingTransfer && (
                    <PendingTransferBannerRow colSpan={colSpan} />
                  )}
                  <TableRow>
                    {cells.map((cell) => (
                      <DataCell
                        key={cell.id}
                        row={row}
                        columnId={cell.column.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </DataCell>
                    ))}
                  </TableRow>
                </Fragment>
              );
            })}
          </>
        )}

        {/* ---- Transferred Incidents section ---- */}
        <SectionSubHeaderRow
          config={transferredConfig}
          colSpan={colSpan}
          {...(isLoading
            ? { type: "loading" }
            : { type: "loaded", count: transferredIncidents.length })}
        />

        {isLoading ? (
          <LoadingSectionRow colSpan={colSpan} />
        ) : transferredRows.length > 0 ? (
          <>
            <ColumnHeaderRow table={table} />
            {transferredRows.map((row) => {
              const tableRow = table
                .getRowModel()
                .rows.find((r) => r.id === row.id);
              if (!tableRow) return null;

              const cells = tableRow.getVisibleCells();

              return (
                <Fragment key={tableRow.id}>
                  {row.isPendingTransfer && (
                    <PendingTransferBannerRow colSpan={colSpan} />
                  )}
                  <TableRow>
                    {cells.map((cell) => (
                      <DataCell
                        key={cell.id}
                        row={row}
                        columnId={cell.column.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </DataCell>
                    ))}
                  </TableRow>
                </Fragment>
              );
            })}
          </>
        ) : (
          <EmptySectionRow
            label={transferredConfig.emptyLabel}
            colSpan={colSpan}
          />
        )}
      </TableBody>
    </Table>
  );
}
